import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { linkSentence, singleThreshold } from "@/lib/format";
import { useBase } from "@/hooks/world";
import { useStore } from "@/model/store";
import { linkFrom, type ModelNode, type Link, type MetricNode, type Step } from "@/model/types";
import { ladderContext, LadderTable } from "./LinkRow";
import { roundSteps, StepLadder } from "./StepLadder";
import { NodePicker } from "@/components/NodePicker";

/** A node picker with search (kept under this name for existing callers). */
export function NodeSelect({
	value,
	onChange,
	nodes,
	placeholder = "Choose…",
	filter,
	autoFocus,
}: {
	value: string | undefined;
	onChange: (id: string) => void;
	nodes: ModelNode[];
	placeholder?: string;
	filter?: (n: ModelNode) => boolean;
	autoFocus?: boolean;
}) {
	return <NodePicker value={value} onChange={onChange} nodes={nodes} filter={filter} placeholder={placeholder} autoFocus={autoFocus} />;
}

type Kind = Link["type"];

/** Calculated nodes can't be linked into; metrics affect metrics through formulas. */
function isTarget(n: ModelNode) {
	return !(n.kind === "event" && n.definition) && !(n.kind === "metric" && n.formula);
}

function canLink(a: ModelNode, b: ModelNode) {
	return a.id !== b.id && isTarget(b) && !(a.kind === "metric" && b.kind === "metric");
}

export function LinkDialog({
	open,
	onOpenChange,
	from: from0,
	to: to0,
	scope,
	edit,
}: {
	open: boolean;
	onOpenChange: (v: boolean) => void;
	from?: string;
	to?: string;
	/** Edit an existing link instead of adding one. */
	edit?: Link;
	/** Limit choices to these node ids (e.g. the model's nodes). */
	scope?: string[];
}) {
	const allNodes = useStore((s) => s.world.nodes);
	const base = useBase();
	const addLink = useStore((s) => s.addLink);
	const replaceLink = useStore((s) => s.replaceLink);
	// Ends that are known from where the dialog was opened are fixed; when editing, both can change.
	const fixedFrom = edit ? undefined : from0;
	const fixedTo = edit ? undefined : to0;
	const nodes = useMemo(() => Object.values(allNodes).filter((n) => !scope || scope.includes(n.id)), [allNodes, scope]);
	const [from, setFrom] = useState(from0);
	const [to, setTo] = useState(to0);
	const [type, setType] = useState<Kind>("trigger");
	const [p, setP] = useState(20);
	const [within, setWithin] = useState(3);
	const [lag, setLag] = useState(0);
	const [x, setX] = useState(2);
	const [limited, setLimited] = useState(false);
	const [forMonths, setForMonths] = useState(12);
	const [steps, setSteps] = useState<Step[]>([]);
	const [stepsDraft, setStepsDraft] = useState<Step[] | null>(null);
	const [change, setChange] = useState<number | null>(null);
	const [over, setOver] = useState(3);
	const [recovers, setRecovers] = useState(false);
	const [recovery, setRecovery] = useState(12);

	// Start from the link being edited, or from defaults, every time the dialog opens.
	const openedFrom = useRef<string | undefined>(undefined);
	// Default ladder: a single threshold near the metric's latest value, 2× as likely above it.
	const suggest = (id?: string): Step[] => {
		const m = id ? allNodes[id] : undefined;
		if (m?.kind !== "metric") return [];
		const v = m.observed.at(-1)?.value ?? Object.values(m.range)[0]?.[1] ?? 1;
		return roundSteps([{ x: 1 }, { from: v, x: 2 }]);
	};
	useEffect(() => {
		if (!open) return;
		const l = edit;
		const start = l ? linkFrom(l) : from0;
		openedFrom.current = start;
		setFrom(start);
		setTo(l ? l.to : to0);
		setType(l?.type ?? "trigger");
		setP(l?.type === "trigger" ? Math.round(l.p * 100) : 20);
		setWithin(l?.type === "trigger" ? l.within_months : 3);
		setLag(l?.type === "trigger" ? l.lag_months : 0);
		const em = l?.type === "multiplier" && !("steps" in l) ? l : null;
		setX(em ? em.x : 2);
		setLimited(em ? !!em.for_months : false);
		setForMonths(em?.for_months ? em.for_months : 12);
		setStepsDraft(null);
		setChange(l?.type === "impact" ? Math.round(l.change * 100) : null);
		setOver(l?.type === "impact" ? l.over_months : 3);
		setRecovers(l?.type === "impact" ? l.recovery_months !== null : false);
		setRecovery(l?.type === "impact" && l.recovery_months ? l.recovery_months : 12);
		setSteps(l?.type === "multiplier" && "steps" in l ? l.steps : suggest(start));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const label = (id: string) => allNodes[id]?.title ?? id;
	const f = from ? allNodes[from] : undefined;
	const t = to ? allNodes[to] : undefined;
	const allowed: Kind[] =
		f && t
			? f.kind === "event" && t.kind === "event"
				? ["trigger", "multiplier"]
				: f.kind === "event" && t.kind === "metric"
					? ["impact"]
					: f.kind === "metric" && t.kind === "event"
						? ["multiplier"]
						: []
			: [];

	// Suggest the link type from the node kinds (PRD §10.6).
	useEffect(() => {
		if (allowed.length && !allowed.includes(type)) setType(allowed[0]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [from, to]);

	// Suggest a threshold from the chosen metric's latest value (not when opening an existing link).
	useEffect(() => {
		if (from === openedFrom.current) return;
		openedFrom.current = from;
		setSteps(suggest(from));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [from]);

	const draft: Link | null = useMemo(() => {
		if (!from || !to || !allowed.includes(type) || from === to) return null;
		// A draft has no id until the API saves it.
		const id = "draft";
		if (type === "trigger") return { id, type, from, to, p: p / 100, within_months: within, lag_months: lag };
		if (type === "impact") {
			// No default size: the sentence waits for a number rather than assuming a drop (review 7.4).
			if (change === null || change === 0) return null;
			return { id, type, from, to, change: change / 100, over_months: over, recovery_months: recovers ? recovery : null };
		}
		if (f?.kind === "metric") {
			if (steps.length < 2) return null;
			return { id, type, from: { metric: from }, to, steps };
		}
		return { id, type, from, to, x, for_months: limited ? forMonths : null };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [from, to, type, p, within, lag, x, limited, forMonths, steps, change, over, recovers, recovery, f]);

	const metricToMetric = f?.kind === "metric" && t?.kind === "metric";

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>
						{edit
							? "Edit link"
							: fixedFrom && fixedTo
								? `Link ${label(fixedFrom)} to ${label(fixedTo)}`
								: fixedFrom
									? `What does ${label(fixedFrom)} affect?`
									: fixedTo
										? `What drives ${label(fixedTo)}?`
										: "Link two nodes"}
					</DialogTitle>
					<DialogDescription>
						Every link reads as one sentence. {fixedFrom && fixedTo ? "Set the numbers." : "Choose the node, then set the numbers."}
					</DialogDescription>
				</DialogHeader>
				{!(fixedFrom && fixedTo) && (
					<div className={fixedFrom || fixedTo ? "" : "grid grid-cols-2 gap-3"}>
						{!fixedFrom && (
							<div className="space-y-1.5">
								<Label>{fixedTo ? "Driven by" : "From"}</Label>
								<NodeSelect
									value={from}
									onChange={setFrom}
									nodes={nodes}
									filter={(n) => (to ? canLink(n, allNodes[to]) : true)}
									autoFocus={!edit && !!fixedTo}
								/>
							</div>
						)}
						{!fixedTo && (
							<div className="space-y-1.5">
								<Label>{fixedFrom ? "Affects" : "To"}</Label>
								<NodeSelect
									value={to}
									onChange={setTo}
									nodes={nodes}
									filter={(n) => (from ? canLink(allNodes[from], n) : isTarget(n))}
									autoFocus={!edit && !fixedTo}
								/>
							</div>
						)}
					</div>
				)}

				{metricToMetric && (
					<p className="rounded-md bg-muted p-3 text-sm">
						Metrics affect other metrics through formulas. Open the target metric and use its formula, for example with <code>lag()</code>.
					</p>
				)}

				{allowed.length > 1 && (
					<ToggleGroup type="single" variant="outline" value={type} onValueChange={(v) => v && setType(v as Kind)} className="justify-start">
						<ToggleGroupItem value="trigger">Trigger: can cause it</ToggleGroupItem>
						<ToggleGroupItem value="multiplier">Multiplier: makes it more or less likely</ToggleGroupItem>
					</ToggleGroup>
				)}

				{draft || allowed.length ? (
					<div className="space-y-3">
						{type === "trigger" && (
							<div className="grid grid-cols-3 gap-3">
								<Field label="Chance it follows (%)" value={p} onChange={setP} min={1} max={100} />
								<Field label="Within (months)" value={within} onChange={setWithin} min={1} max={60} />
								<Field label="After a delay of (months)" value={lag} onChange={setLag} min={0} max={36} />
							</div>
						)}
						{type === "multiplier" && f?.kind === "event" && (
							<div className="grid grid-cols-2 items-end gap-3">
								<Field label="Times as likely" value={x} onChange={setX} min={0} max={20} step={0.1} />
								<div className="space-y-1.5">
									<label className="flex items-center gap-2 text-sm">
										<Switch checked={limited} onCheckedChange={setLimited} /> Only for a while
									</label>
									{limited && (
										<Input type="number" value={forMonths} min={1} max={120} onChange={(e) => setForMonths(Number(e.target.value))} aria-label="Months" />
									)}
								</div>
							</div>
						)}
						{type === "multiplier" && f?.kind === "metric" && (
							<div className="space-y-1.5">
								<Label className="text-xs">How much more or less likely, by the level of {f.title}</Label>
								<StepLadder
									steps={stepsDraft ?? steps}
									unit={(f as MetricNode).unit}
									scale={(f as MetricNode).scale}
									context={ladderContext(f as MetricNode, base.data)}
									onChange={setStepsDraft}
									onCommit={(s2) => {
										setStepsDraft(null);
										setSteps(s2);
									}}
									height={150}
								/>
							</div>
						)}
						{type === "impact" && (
							<div className="grid grid-cols-3 items-end gap-3">
								<Field label="Changes by (%)" value={change} onChange={setChange} min={-100} max={300} placeholder="+15 or −20" autoFocus={!edit} />
								<Field label="Over (months)" value={over} onChange={setOver} min={1} max={36} />
								<div className="space-y-1.5">
									<label className="flex items-center gap-2 text-sm">
										<Switch checked={recovers} onCheckedChange={setRecovers} /> Recovers
									</label>
									{recovers && (
										<Input
											type="number"
											value={recovery}
											min={1}
											max={120}
											onChange={(e) => setRecovery(Number(e.target.value))}
											aria-label="Recovery months"
										/>
									)}
								</div>
							</div>
						)}
					</div>
				) : null}

				{draft ? (
					<div className="rounded-md border-l-4 border-ink/30 bg-accent/60 px-3 py-2 font-serif text-[15px]">
						{draft.type === "multiplier" && "steps" in draft && draft.steps.length > 2 && !singleThreshold(draft.steps) ? (
							<>
								<p>
									How likely {label(draft.to)} is depends on {label(draft.from.metric)}:
								</p>
								<LadderTable steps={draft.steps} unit={(allNodes[draft.from.metric] as MetricNode | undefined)?.unit ?? ""} />
							</>
						) : (
							<p>{linkSentence(draft, allNodes)}</p>
						)}
					</div>
				) : (
					type === "impact" &&
					from &&
					to &&
					allowed.includes("impact") && (
						<p className="rounded-md border-l-4 border-border bg-accent/40 px-3 py-2 font-serif text-[15px] text-muted-foreground">
							If {label(from)} happens, {label(to)} changes by <span className="font-sans text-ink">__%</span> over {over} {over === 1 ? "month" : "months"}.
							Type a change to add the link: + for a rise, − for a drop.
						</p>
					)
				)}

				<DialogFooter>
					<Button variant="ghost" onClick={() => onOpenChange(false)}>
						Cancel
					</Button>
					<Button
						disabled={!draft}
						onClick={() => {
							if (!draft) return;
							void (edit ? replaceLink(edit.id, { ...draft, id: edit.id }) : addLink(draft)).then((ok) => ok && onOpenChange(false));
						}}
					>
						{edit ? "Save link" : "Add link"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function Field<V extends number | null>({
	label,
	value,
	onChange,
	min,
	max,
	step = 1,
	placeholder,
	autoFocus,
}: {
	label: string;
	value: V;
	onChange: (v: number) => void;
	min: number;
	max: number;
	step?: number;
	placeholder?: string;
	autoFocus?: boolean;
}) {
	// Keep the typed text so the box can be cleared while typing; only valid numbers are passed up.
	const show = (v: V) => (v === null ? "" : String(v));
	const [text, setText] = useState(show(value));
	useEffect(() => {
		if (parseFloat(text.replace(/^[−–]/, "-")) !== value) setText(show(value));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);
	return (
		<div className="space-y-1.5">
			<Label className="text-xs">{label}</Label>
			<Input
				inputMode="decimal"
				value={text}
				placeholder={placeholder}
				autoFocus={autoFocus}
				onChange={(e) => {
					setText(e.target.value);
					const v = parseFloat(e.target.value.replace(/^[−–]/, "-").replace(/[+%\s]/g, ""));
					if (Number.isFinite(v)) onChange(Math.min(max, Math.max(min, v)));
				}}
				onKeyDown={(e) => {
					if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
					e.preventDefault();
					const v = (value ?? 0) + (e.key === "ArrowUp" ? step : -step);
					onChange(Math.round(Math.min(max, Math.max(min, v)) * 100) / 100);
				}}
				onBlur={() => setText(show(value))}
			/>
		</div>
	);
}
